import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getProducts, getPageSummaries, updateProduct, deleteProduct } from './api';
import { useDomain } from './DomainContext';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import Alert from './ui/Alert';
import Badge from './ui/Badge';
import Spinner from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';
import SiteSectionNav from './ui/SiteSectionNav';

const UNATTACHED = '__inbox__';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', margin: '0 4px 4px 0',
  border: '1px solid var(--color-border)', borderRadius: 999, fontSize: 12, background: 'var(--color-surface)',
};

/** Notes cell: edits in place, saves on blur when changed. */
const NotesCell = ({ value, onSave }) => {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  return (
    <textarea
      className="input"
      rows={2}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft !== (value || '')) onSave(draft); }}
      aria-label="Notes"
      style={{ minWidth: 180, fontSize: 12 }}
    />
  );
};

/**
 * Per-site product library: products saved from Amazon with the Save to DAM
 * extension. Images are hotlinked from Amazon's CDN, never copied.
 */
const DomainProducts = () => {
  const { domainId } = useParams();
  const [searchParams] = useSearchParams();
  const focusAsin = searchParams.get('asin');
  const navigate = useNavigate();
  const { domains, selectedDomain } = useDomain();
  const [products, setProducts] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(searchParams.get('page') || '');
  const [pendingDelete, setPendingDelete] = useState(null);
  const focusRef = useRef(null);

  const domain = selectedDomain?.id === domainId ? selectedDomain : domains.find((d) => d.id === domainId) || null;
  const hostname = domain?.hostname || domain?.name || domainId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [prods, pageList] = await Promise.all([getProducts(domainId), getPageSummaries(domainId).catch(() => [])]);
        if (cancelled) return;
        setProducts(prods);
        setPages(pageList);
      } catch (err) {
        if (err.response?.status === 401) navigate('/login');
        else if (!cancelled) setError(errText(err, 'Failed to load products'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [domainId, navigate]);

  useEffect(() => {
    if (!loading && focusRef.current) focusRef.current.scrollIntoView({ block: 'center' });
  }, [loading]);

  const pageTitle = useMemo(() => {
    const m = {};
    pages.forEach((p) => { m[p.slug] = { title: p.title || p.slug, draft: p.status === 'draft' }; });
    return m;
  }, [pages]);

  const usedPages = useMemo(() => {
    const s = new Set();
    products.forEach((p) => (p.pages || []).forEach((slug) => s.add(slug)));
    return [...s].sort((a, b) => (pageTitle[a]?.title || a).localeCompare(pageTitle[b]?.title || b));
  }, [products, pageTitle]);

  const shown = products.filter((p) => {
    if (!filter) return true;
    if (filter === UNATTACHED) return !(p.pages || []).length;
    return (p.pages || []).includes(filter);
  });

  const apply = async (asin, changes) => {
    setError(null);
    try {
      const res = await updateProduct(domainId, asin, changes);
      setProducts((prev) => prev.map((p) => (p.asin === asin ? { ...p, ...res.product } : p)));
    } catch (err) {
      setError(errText(err, 'Update failed'));
    }
  };

  const confirmDelete = async () => {
    const asin = pendingDelete?.asin;
    setPendingDelete(null);
    if (!asin) return;
    try {
      await deleteProduct(domainId, asin);
      setProducts((prev) => prev.filter((p) => p.asin !== asin));
    } catch (err) {
      setError(errText(err, 'Delete failed'));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Products"
        subtitle={`${hostname}: Amazon products saved with the Save to DAM extension`}
      />
      <SiteSectionNav section="growth" />

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {!loading && domain && !domain.amazon_tag && (
        <Alert type="warning">
          This site has no Amazon tracking tag, so the extension can't save to it yet. Set one under Site settings &gt; Domain info.
        </Alert>
      )}

      <div className="toolbar">
        <label className="sr-only" htmlFor="products-filter">Filter by page</label>
        <select id="products-filter" className="select" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All products ({products.length})</option>
          <option value={UNATTACHED}>Inbox: no page yet</option>
          {usedPages.map((slug) => (
            <option key={slug} value={slug}>
              {pageTitle[slug]?.title || slug}{pageTitle[slug]?.draft ? ' (draft)' : ''}
            </option>
          ))}
        </select>
        <span className="muted small">{shown.length} shown</span>
      </div>

      {loading ? (
        <Spinner label="Loading products…" />
      ) : shown.length === 0 ? (
        <EmptyState
          title={products.length ? 'No products match' : 'No products yet'}
          message={products.length ? 'Try another page filter.'
            : 'Install the Save to DAM extension (extension/ in the repo; token under Settings) and save products from Amazon.'}
        />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 72 }}>Image</th>
                <th>Product</th>
                <th>Rating</th>
                <th>Band</th>
                <th>Pages</th>
                <th>Notes</th>
                <th>Captured</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => {
                const focused = p.asin === focusAsin;
                const attachable = pages.filter((pg) => !(p.pages || []).includes(pg.slug));
                return (
                  <tr key={p.asin} ref={focused ? focusRef : null}
                      style={focused ? { outline: '2px solid var(--color-primary)', outlineOffset: -2 } : undefined}>
                    <td>
                      {p.image_url ? (
                        <img src={p.image_url} alt="" loading="lazy" referrerPolicy="no-referrer"
                             style={{ width: 64, height: 64, objectFit: 'contain' }} />
                      ) : <span className="muted small">none</span>}
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      <div style={{ fontWeight: 500 }}>{p.title || <span className="muted">(untitled)</span>}</div>
                      <div className="muted small">
                        {p.brand ? `${p.brand} · ` : ''}
                        <a href={p.detail_url} target="_blank" rel="noopener noreferrer">{p.asin}</a>
                        {' · '}
                        <Badge state={p.status === 'used' ? 'success' : 'info'}>{p.status || 'inbox'}</Badge>
                      </div>
                    </td>
                    <td className="small">
                      {p.rating != null ? `${p.rating}★` : '—'}
                      {p.review_count != null && <div className="muted">{Number(p.review_count).toLocaleString()}</div>}
                    </td>
                    <td>{p.price_band || '—'}</td>
                    <td style={{ minWidth: 180 }}>
                      {(p.pages || []).map((slug) => (
                        <span key={slug} style={chipStyle}>
                          <Link to={`/domain/${domainId}/pages/${encodeURIComponent(slug)}/edit`}>
                            {pageTitle[slug]?.title || slug}
                          </Link>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ padding: '0 2px' }}
                                  aria-label={`Detach from ${slug}`} onClick={() => apply(p.asin, { detach_page: slug })}>
                            ×
                          </button>
                        </span>
                      ))}
                      <select className="select" value="" aria-label="Attach to page" style={{ fontSize: 12, marginTop: 2 }}
                              onChange={(e) => e.target.value && apply(p.asin, { attach_page: e.target.value })}>
                        <option value="">+ attach to page…</option>
                        {attachable.map((pg) => (
                          <option key={pg.slug} value={pg.slug}>
                            {pg.title || pg.slug}{pg.status === 'draft' ? ' (draft)' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td><NotesCell value={p.notes} onSave={(notes) => apply(p.asin, { notes })} /></td>
                    <td className="muted small">
                      {fmtDate(p.captured_at)}
                      {p.captured_by && <div>{p.captured_by}</div>}
                    </td>
                    <td className="actions">
                      <button type="button" className="btn btn-secondary btn-sm"
                              onClick={() => apply(p.asin, { status: p.status === 'used' ? 'inbox' : 'used' })}>
                        {p.status === 'used' ? 'Mark inbox' : 'Mark used'}
                      </button>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => setPendingDelete(p)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete product"
        message={`Remove ${pendingDelete?.asin || ''} from this site's library? Pages that mention it are not changed.`}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default DomainProducts;
